import { useState, useRef } from "react";
import instance from "../../Interceptor/api_instance";
import { Dots } from "../../Components/LoadingAnimations/LoadingAnimations";
import { DeleteIcon } from "../../Media/Icons";
import { normalizeImageFile } from "../../Components/Utility/HeicToJpg";
import ESignature from "../../Components/Inputs/ESignature";
import { useTranslation } from "react-i18next";
import { FormGroup } from "../../Components/Inputs/FormGroup";
import { useAuth } from "../../Auth/AuthProvider";
import { ActionResponse } from "../../Components/Modals/ActionResponses";

export const CheckInVisitor = ({ onClose, english }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [formData, setFormData] = useState({
    photos: [],
    firstName: null,
    lastName: null,
    company: null,
    badgeNum: null,
    idNum: null,
    personVisiting: null,
    category: null,
    licensePlate: null,
    employee_pin: null,
  });
  const [responseModal, setResponseModal] = useState(null);

  const MAX_FILE_SIZE = 2.5 * 1024 * 1024; // 2MB

  const submit = async (e) => {
    e.preventDefault();
    try {
      // Validate form data
      setIsLoading(true);

      const formDataToSend = new FormData();

      if (formData.signature) {
        const blob = await (await fetch(formData.signature)).blob();
        formDataToSend.append("signature", blob);
      } else if (
        formData.category === "Provider" ||
        formData.category === "Visitor"
      ) {
        throw new Error(t("errors.noSignature")).message;
      }

      formData.photos.forEach((photo, index) => {
        formDataToSend.append("photo", photo);
      });

      const data = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        company: formData.company,
        badge_num: formData.badgeNum,
        id_num: formData.idNum,
        person_visiting: formData.personVisiting,
        category: formData.category,
        license_plate: formData.licensePlate,
        employee_pin: formData.employee_pin,
      };
      formDataToSend.append("data", JSON.stringify(data));
      console.log(formDataToSend);
      const response = await instance.post(
        "assets/check-in-visitor/",
        formDataToSend,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status === 200) {
        setResponseModal({
          status: t("forms.type.success"),
          message: t("forms.responses.success"),
        });
      }
    } catch (e) {
      setResponseModal({
        status: t("forms.type.error"),
        message: e,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCapturePhoto = async (e) => {
    try {
      if (!e.target.files || e.target.files.length === 0) {
        return;
      }

      const file = e.target.files[0];
      const convertedFile = await normalizeImageFile(file);

      setFormData((prevFormData) => ({
        ...prevFormData,
        photos: [...prevFormData.photos, convertedFile],
      }));
    } catch (error) {
      console.error("Photo conversion failed:", error);
    } finally {
      e.target.value = "";
    }
  };

  const handleUploadPhoto = async (e) => {
    if (e.target.files.length === 0) {
      return;
    }
    const filesArray = Array.from(e.target.files);
    const validFiles = [];

    for (const file of filesArray) {
      const convertedFile = await normalizeImageFile(file);
      validFiles.push(convertedFile);
    }

    if (validFiles.length > 0) {
      setFormData((prev) => ({
        ...prev,
        photos: [...prev.photos, ...validFiles],
      }));
    }
  };

  // Function to handle upload button click
  const handleUploadClick = (e) => {
    e.preventDefault(); // Prevent form submission
    if (fileInputRef.current) {
      fileInputRef.current.click(); // Ensure fileInputRef is not null before accessing click method
    }
  };

  const updateFormData = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const deletePhoto = (index) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      photos: prevFormData.photos.filter((_, i) => i !== index),
    }));
  };

  return (
    <div className="prompt-cont">
      {responseModal && (
        <ActionResponse
          status={responseModal.status}
          message={responseModal.message}
          close={() => {
            if (responseModal.status === t("forms.type.success")) {
              setResponseModal(null);
              onClose();
            } else {
              setResponseModal(null);
            }
          }}
        />
      )}
      <h2 className="global-h2 light-black">
        {t("forms.type.visitor.header-in")}
      </h2>
      <h4 className="global-h4 gray bottom-space">
        {t("forms.type.vehicle.sub-header.firstForm")}
      </h4>
      <form id="form-submit" onSubmit={submit}>
        <div className="form-group">
          <label className="input-label required">{t("buttons.visitor")}</label>
          <div className="input-cont">
            <input
              type="text"
              placeholder={t("forms.type.newDriver.input-headers.firstName")}
              className="text-input"
              name="firstName"
              onChange={updateFormData}
              value={formData.firstName}
              required
            />
            <input
              type="text"
              placeholder={t("forms.type.newDriver.input-headers.lastName")}
              className="text-input"
              name="lastName"
              onChange={updateFormData}
              value={formData.lastName}
              required
            />
          </div>
        </div>
        <div className="form-group">
          <label className="input-label required">
            {t("forms.type.visitor.purpose.header")}
          </label>
          <div className="input-cont">
            <select
              onChange={updateFormData}
              name="category"
              value={formData.category}
              required
            >
              <option value="" selected>
                {t("forms.type.visitor.purpose.select")}
              </option>
              <option value="Visitor">{t("buttons.visitor")}</option>
              <option value="Provider">
                {t("forms.type.visitor.purpose.provider")}
              </option>
              <option value="Driver">
                {t("forms.type.visitor.purpose.driver")}
              </option>
              <option value="Mechanic">
                {t("forms.type.visitor.purpose.mechanic")}
              </option>
              <option value="Staff">
                {t("forms.type.visitor.purpose.staff")}
              </option>
              <option value="Guard">
                {t("forms.type.visitor.purpose.guard")}
              </option>
            </select>
          </div>
        </div>
        {/* Input fields for provider visitor */}
        {["Provider", "Visitor"].includes(formData.category) && (
          <div>
            <FormGroup
              type={"text"}
              value={formData.company}
              label={t("yard-tables.table-columns.company")}
              name={"company"}
              onChange={updateFormData}
              isRequired={true}
              placeholder={"N/A"}
            />
            <FormGroup
              type={"text"}
              value={formData.licensePlate}
              label={t("yard-tables.table-columns.vehicle-plates")}
              name={"licensePlate"}
              onChange={updateFormData}
              isRequired={true}
              placeholder={"N/A"}
            />
            <FormGroup
              type={"text"}
              value={formData.personVisiting}
              label={t("forms.type.f12.person-visiting")}
              name={"personVisiting"}
              onChange={updateFormData}
              isRequired={true}
              placeholder={"N/A"}
            />
            <FormGroup
              type={"text"}
              value={formData.idNum}
              label={t("forms.type.f12.id-num")}
              name={"idNum"}
              onChange={updateFormData}
              isRequired={true}
              placeholder={"N/A"}
            />
            <FormGroup
              type={"text"}
              value={formData.badgeNum}
              label={t("forms.type.f12.badge-num")}
              name={"badgeNum"}
              onChange={updateFormData}
              isRequired={true}
              placeholder={"N/A"}
            />
            <div className="form-group">
              <label className="input-label required">
                {t("forms.type.visitor.signature")}
              </label>
              <div className="input-cont">
                <ESignature setFormData={setFormData} formData={formData} />
              </div>
            </div>
          </div>
        )}
        {user.location.loc_country === "Mexico" && (
          <div className="form-group">
            <label className="input-label required">Pin #</label>
            <div className="input-cont">
              <input
                className="text-input"
                placeholder="Pin #"
                type="text"
                inputMode="numeric" // mobile shows number keypad
                pattern="[0-9]*" // hint for browsers/autofill
                autoComplete="one-time-code"
                value={formData.employee_pin}
                onChange={(e) => {
                  const digitsOnly = e.target.value.replace(/\D/g, "");
                  setFormData((prevData) => ({
                    ...prevData,
                    employee_pin: digitsOnly,
                  }));
                }}
                name="employee_pin"
                required
              />
            </div>
          </div>
        )}
        <div className="driver-photo-id">
          <h2 className="gray">
            {english("forms.type.vehicle.sub-header.photos")}
          </h2>
          <div
            data-testid="profile-photo"
            className="visitor-photo margin-bottom-24"
          >
            {/* Display the uploaded photos */}
            {formData.photos.map((photo, index) => (
              <div key={index} className="photo-preview">
                <div className="delete-icon" onClick={() => deletePhoto(index)}>
                  <DeleteIcon />
                </div>
                <img
                  src={URL.createObjectURL(photo)}
                  alt={`Uploaded ${index + 1}`}
                />
              </div>
            ))}
          </div>
          <div className="align-center gap-16 margin-bottom-24">
            <div>
              {/* Hidden file input */}
              <input
                data-testid="file-input"
                className="file-input"
                type="file"
                name="photo"
                onChange={handleUploadPhoto}
                accept="image/*, .heic"
                ref={fileInputRef}
                multiple
              />
              <button
                onClick={handleUploadClick}
                className="profile-pic-btn"
                type="button"
              >
                {english("buttons.uploadPhoto")}
              </button>
            </div>
            <div onClick={() => cameraInputRef.current.click()}>
              <input
                data-testid="file-input"
                className="file-input"
                type="file"
                capture="environment"
                name="photo"
                onChange={handleCapturePhoto}
                accept="image/*"
                ref={cameraInputRef}
              />
              <button className="profile-pic-btn" type="button">
                {t("forms.type.vehicle.input-headers.takePhoto")}
              </button>
            </div>
          </div>
        </div>
        <div className="full-cont">
          {isLoading ? (
            <Dots />
          ) : (
            <button type="submit" className="change-pwd-btn">
              {english("buttons.submit")}
            </button>
          )}

          <button onClick={onClose} type="button" className="cancel-btn">
            {english("buttons.cancel")}
          </button>
        </div>
      </form>
    </div>
  );
};
